import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { authAPI } from '../api/api';
import chatAPI from '../api/chatAPI';
import webSocketService from '../services/WebSocketService';

// Chat állapot kezdeti értékei
const initialState = {
  // Felhasználó
  currentUser: null,
  
  // Chat listák
  chats: [],
  activeChat: null,
  
  // Üzenetek
  messages: {},
  loadingMessages: {},
  hasMoreMessages: {},
  
  // UI állapotok
  loading: false,
  error: null,
  
  // WebSocket állapot
  connectionStatus: 'disconnected',
  isReconnecting: false,
  
  // Typing státusz
  typingUsers: {},
  
  // Olvasatlan üzenetek
  unreadCounts: {}
};

// Action típusok
const actionTypes = {
  SET_CURRENT_USER: 'SET_CURRENT_USER',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  
  // Chat műveletek
  SET_CHATS: 'SET_CHATS',
  ADD_CHAT: 'ADD_CHAT',
  SET_ACTIVE_CHAT: 'SET_ACTIVE_CHAT',
  UPDATE_CHAT: 'UPDATE_CHAT',
  
  // Üzenet műveletek
  SET_MESSAGES: 'SET_MESSAGES',
  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  PREPEND_MESSAGES: 'PREPEND_MESSAGES',
  SET_LOADING_MESSAGES: 'SET_LOADING_MESSAGES',
  SET_HAS_MORE_MESSAGES: 'SET_HAS_MORE_MESSAGES',
  
  // WebSocket
  SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS',
  
  // Typing
  SET_TYPING_USER: 'SET_TYPING_USER',
  REMOVE_TYPING_USER: 'REMOVE_TYPING_USER',
  
  // Olvasatlan üzenetek
  SET_UNREAD_COUNT: 'SET_UNREAD_COUNT',
  CLEAR_UNREAD_COUNT: 'CLEAR_UNREAD_COUNT'
};

// Reducer függvény
function chatReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_CURRENT_USER:
      return { ...state, currentUser: action.payload };
      
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
      
    case actionTypes.SET_ERROR:
      return { ...state, error: action.payload };
      
    case actionTypes.SET_CHATS:
      return { ...state, chats: action.payload };
      
    case actionTypes.ADD_CHAT:
      return { 
        ...state, 
        chats: [...state.chats, action.payload] 
      };
      
    case actionTypes.SET_ACTIVE_CHAT:
      return { ...state, activeChat: action.payload };
      
    case actionTypes.UPDATE_CHAT:
      return {
        ...state,
        chats: state.chats.map(chat =>
          chat.chat_id === action.payload.chat_id
            ? { ...chat, ...action.payload }
            : chat
        )
      };
      
    case actionTypes.SET_MESSAGES:
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.chatId]: action.payload.messages
        }
      };
      
    case actionTypes.ADD_MESSAGE:
      const chatId = action.payload.chat_id;
      return {
        ...state,
        messages: {
          ...state.messages,
          [chatId]: [...(state.messages[chatId] || []), action.payload]
        }
      };
      
    case actionTypes.UPDATE_MESSAGE:
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.chatId]: state.messages[action.payload.chatId]?.map(msg =>
            msg.temp_id === action.payload.temp_id || msg.id === action.payload.id
              ? { ...msg, ...action.payload.updates }
              : msg
          ) || []
        }
      };
      
    case actionTypes.PREPEND_MESSAGES:
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.chatId]: [
            ...action.payload.messages,
            ...(state.messages[action.payload.chatId] || [])
          ]
        }
      };
      
    case actionTypes.SET_LOADING_MESSAGES:
      return {
        ...state,
        loadingMessages: {
          ...state.loadingMessages,
          [action.payload.chatId]: action.payload.loading
        }
      };
      
    case actionTypes.SET_HAS_MORE_MESSAGES:
      return {
        ...state,
        hasMoreMessages: {
          ...state.hasMoreMessages,
          [action.payload.chatId]: action.payload.hasMore
        }
      };
      
    case actionTypes.SET_CONNECTION_STATUS:
      return { 
        ...state, 
        connectionStatus: action.payload.status,
        isReconnecting: action.payload.isReconnecting || false
      };
      
    case actionTypes.SET_TYPING_USER:
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [action.payload.chatId]: {
            ...state.typingUsers[action.payload.chatId],
            [action.payload.userId]: {
              userName: action.payload.userName,
              timestamp: Date.now()
            }
          }
        }
      };
      
    case actionTypes.REMOVE_TYPING_USER:
      const updatedTyping = { ...state.typingUsers };
      if (updatedTyping[action.payload.chatId]) {
        delete updatedTyping[action.payload.chatId][action.payload.userId];
        if (Object.keys(updatedTyping[action.payload.chatId]).length === 0) {
          delete updatedTyping[action.payload.chatId];
        }
      }
      return { ...state, typingUsers: updatedTyping };
      
    case actionTypes.SET_UNREAD_COUNT:
      return {
        ...state,
        unreadCounts: {
          ...state.unreadCounts,
          [action.payload.chatId]: action.payload.count
        }
      };
      
    case actionTypes.CLEAR_UNREAD_COUNT:
      const updatedCounts = { ...state.unreadCounts };
      delete updatedCounts[action.payload.chatId];
      return { ...state, unreadCounts: updatedCounts };
      
    default:
      return state;
  }
}

// Context létrehozása
const ChatContext = createContext();

// Context Provider komponens
export function ChatProvider({ children }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  // Chat API ready állapot
  const [chatAPIReady, setChatAPIReady] = React.useState(false);

  // Felhasználó inicializálása
  useEffect(() => {
    const user = authAPI.getCurrentUser();
    if (user) {
      // Ensure user has id property (normalize user_id to id)
      const normalizedUser = {
        ...user,
        id: user.id || user.user_id
      };
      dispatch({ type: actionTypes.SET_CURRENT_USER, payload: normalizedUser });

      // Set client user ID for chat API
      chatAPI.setClientUserId(normalizedUser.id);

      // Mark chat API as ready
      setChatAPIReady(true);
    }
  }, []);

  // Állapot referencia a legfrissebb értékekhez (WebSocket handlerhez)
  const stateRef = React.useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // WebSocket üzenetek kezelése - stabil handler (nem okoz effect újrafutást)
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'connection_established':
        return;

      case 'joined_chat':
        return;

      case 'new_chat_created':
        dispatch({ type: actionTypes.ADD_CHAT, payload: data.chat });
        return;

      case 'user_left':
        return;

      case 'new_message': {
        const current = stateRef.current;
        
        // Ne adjuk hozzá a saját üzeneteket WebSocket-en keresztül, mert azok már az API válaszból jönnek
        if (data.user_id === current.currentUser?.id) {
          return;
        }
        
        // Ellenőrizzük, hogy az üzenet már létezik-e (duplikáció elkerülése)
        const chatMessages = current.messages[data.chat_id] || [];
        const existingMessage = chatMessages.find(m => m.id === data.message_id);
        
        if (existingMessage) {
          return;
        }
        
        // Üzenet hozzáadása a megfelelő chat-hez
        dispatch({
          type: actionTypes.ADD_MESSAGE,
          payload: {
            id: data.message_id || `ws_${Date.now()}_${Math.random()}`,
            chat_id: data.chat_id,
            user_id: data.user_id,
            user_name: data.user_name,
            message: data.message,
            message_type: data.message_type || 'text',
            created_at: normalizeTimestamp(data.timestamp)
          }
        });

        // Chat lista frissítése az utolsó üzenettel
        dispatch({
          type: actionTypes.UPDATE_CHAT,
          payload: {
            chat_id: data.chat_id,
            last_message: data.message,
            last_message_time: normalizeTimestamp(data.timestamp)
          }
        });

        // Olvasatlan növelése, ha nem az aktív chat és nem a saját üzenet
        if (current.activeChat?.chat_id !== data.chat_id && data.user_id !== current.currentUser?.id) {
          const currentCount = current.unreadCounts[data.chat_id] || 0;
          dispatch({
            type: actionTypes.SET_UNREAD_COUNT,
            payload: { chatId: data.chat_id, count: currentCount + 1 }
          });
        }
        return;
      }

      case 'typing_start': {
        const current = stateRef.current;
        if (data.user_id !== current.currentUser?.id) {
          dispatch({
            type: actionTypes.SET_TYPING_USER,
            payload: { chatId: data.chat_id, userId: data.user_id, userName: data.user_name }
          });
        }
        return;
      }

      case 'typing_stop': {
        const current = stateRef.current;
        if (data.user_id !== current.currentUser?.id) {
          dispatch({
            type: actionTypes.REMOVE_TYPING_USER,
            payload: { chatId: data.chat_id, userId: data.user_id }
          });
        }
        return;
      }

      default:
        return;
    }
  }, []);

  // WebSocket kapcsolat kezelése - csak felhasználó váltáskor fusson
  useEffect(() => {
    if (!state.currentUser) return;

    // Kapcsolódás
    webSocketService.connect().catch(() => {
      // szerver lehet nem fut; csend
    });

    // Kapcsolat státusz figyelése + automatikus re-join aktív szobába reconnect után
    const removeConnectionHandler = webSocketService.addConnectionHandler((status) => {
      dispatch({
        type: actionTypes.SET_CONNECTION_STATUS,
        payload: { status, isReconnecting: status === 'reconnecting' }
      });

      if (status === 'connected') {
        const current = stateRef.current;
        if (current.activeChat && current.currentUser) {
          webSocketService.joinChat(
            current.activeChat.chat_id,
            current.currentUser.id,
            current.currentUser.name
          );
        }
      }
    });

    // Üzenet figyelése
    const removeMessageHandler = webSocketService.addMessageHandler(handleWebSocketMessage);

    return () => {
      removeConnectionHandler();
      removeMessageHandler();
      webSocketService.disconnect();
    };
  }, [state.currentUser, handleWebSocketMessage]);

  // Üzenetek betöltése
  const loadMessages = useCallback(async (chatId, offset = 0) => {
    try {
      dispatch({
        type: actionTypes.SET_LOADING_MESSAGES,
        payload: { chatId, loading: true }
      });

      const messages = await chatAPI.getMessages(chatId, state.currentUser.id, 50, offset);
      
      if (offset === 0) {
        // Első betöltés
        dispatch({
          type: actionTypes.SET_MESSAGES,
          payload: { chatId, messages }
        });
      } else {
        // További üzenetek hozzáfűzése
        dispatch({
          type: actionTypes.PREPEND_MESSAGES,
          payload: { chatId, messages }
        });
      }

      // Van-e még több üzenet
      dispatch({
        type: actionTypes.SET_HAS_MORE_MESSAGES,
        payload: { chatId, hasMore: messages.length === 50 }
      });

    } catch (error) {
      console.error('Hiba az üzenetek betöltésekor:', error);
      dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
    } finally {
      dispatch({
        type: actionTypes.SET_LOADING_MESSAGES,
        payload: { chatId, loading: false }
      });
    }
  }, [state.currentUser]);

  // Chatlisták betöltése
  const loadChats = useCallback(async () => {
    if (!state.currentUser) return;

    try {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      const chats = await chatAPI.getChats(state.currentUser.id);
      dispatch({ type: actionTypes.SET_CHATS, payload: chats });

      // Set unread counts from chat data
      chats.forEach(chat => {
        if (chat.unread_count > 0) {
          dispatch({
            type: actionTypes.SET_UNREAD_COUNT,
            payload: { chatId: chat.chat_id, count: chat.unread_count }
          });
        }
      });
    } catch (error) {
      dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
    } finally {
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
    }
  }, [state.currentUser]);

  // Chat lista automatikus betöltése
  useEffect(() => {
    if (state.currentUser) {
      loadChats();
    }
  }, [state.currentUser, loadChats]);

  // Chat lista periodikus frissítése (olvasatlan számok frissítéséhez)
  useEffect(() => {
    // Only start polling when chat API is ready
    if (!chatAPIReady || !state.currentUser) return;

    const CHAT_LIST_POLL_INTERVAL = 3000; // 3 másodpercenként

    const pollChatList = async () => {
      try {
        const chats = await chatAPI.getChats(state.currentUser.id);
        dispatch({ type: actionTypes.SET_CHATS, payload: chats });

        // Frissítjük az olvasatlan számokat
        chats.forEach(chat => {
          if (chat.unread_count > 0) {
            dispatch({
              type: actionTypes.SET_UNREAD_COUNT,
              payload: { chatId: chat.chat_id, count: chat.unread_count }
            });
          } else {
            // Ha már nincs olvasatlan, töröljük a számlálót
            dispatch({
              type: actionTypes.CLEAR_UNREAD_COUNT,
              payload: { chatId: chat.chat_id }
            });
          }
        });
      } catch (error) {
        console.error('Chat list polling error:', error);
      }
    };

    // Első polling azonnal
    pollChatList();

    const intervalId = setInterval(pollChatList, CHAT_LIST_POLL_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [chatAPIReady, state.currentUser]);

  // Automatikus üzenet polling az aktív chathez
  useEffect(() => {
    if (!chatAPIReady || !state.activeChat || !state.currentUser) return;

    const POLL_INTERVAL = 3000; // 3 másodpercenként

    const pollMessages = async () => {
      try {
        const chatId = state.activeChat.chat_id;
        const currentMessages = state.messages[chatId] || [];

        // Lekérjük a legfrissebb üzeneteket (mark_as_read=false, hogy ne nullázza az unread count-ot)
        const newMessages = await chatAPI.getMessages(chatId, state.currentUser.id, 50, 0, false);

        // Ha van új üzenet, frissítjük
        if (newMessages && newMessages.length > 0) {
          const lastCurrentMessageId = currentMessages.length > 0
            ? currentMessages[currentMessages.length - 1]?.id
            : null;
          const lastNewMessageId = newMessages[newMessages.length - 1]?.id;

          // Csak akkor frissítünk, ha tényleg van új üzenet
          if (lastCurrentMessageId !== lastNewMessageId) {
            dispatch({
              type: actionTypes.SET_MESSAGES,
              payload: { chatId, messages: newMessages }
            });
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    // NE hívjuk meg azonnal - várjunk 1 másodpercet hogy a loadMessages mark-as-read lefusson
    let intervalId = null;

    const timeoutId = setTimeout(() => {
      pollMessages(); // Első poll 1 mp után
      intervalId = setInterval(pollMessages, POLL_INTERVAL);
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [chatAPIReady, state.activeChat, state.currentUser, state.messages]);

  // Chat kiválasztása (szimpla join + üzenetbetöltés)
  const selectChat = useCallback(async (chat) => {
    if (state.activeChat && state.currentUser) {
      webSocketService.leaveChat(
        state.activeChat.chat_id,
        state.currentUser.id,
        state.currentUser.name
      );
    }

    dispatch({ type: actionTypes.SET_ACTIVE_CHAT, payload: chat });

    dispatch({
      type: actionTypes.CLEAR_UNREAD_COUNT,
      payload: { chatId: chat.chat_id }
    });

    if (state.currentUser) {
      webSocketService.joinChat(
        chat.chat_id,
        state.currentUser.id,
        state.currentUser.name
      );
    }

    if (!state.messages[chat.chat_id]) {
      await loadMessages(chat.chat_id);
    }
  }, [state.messages, state.activeChat, state.currentUser, loadMessages]);

  // Automatikus első chat kiválasztása
  useEffect(() => {
    if (!state.activeChat && state.chats.length > 0 && state.currentUser) {
      selectChat(state.chats[0]);
    }
  }, [state.chats, state.activeChat, state.currentUser, selectChat]);

  // Üzenet küldése
  const sendMessage = useCallback(async (chatId, message, messageType = 'text') => {
    if (!state.currentUser || !message.trim()) return;

    const tempId = `temp_${Date.now()}`;
    const tempMessage = {
      temp_id: tempId,
      chat_id: chatId,
      user_id: state.currentUser.id,
      user_name: state.currentUser.name,
      message: message.trim(),
      message_type: messageType,
      created_at: new Date().toISOString(),
      sending: true
    };

    // Optimista frissítés
    dispatch({ type: actionTypes.ADD_MESSAGE, payload: tempMessage });

    try {
      // API hívás
      const result = await chatAPI.sendMessage({
        chat_id: chatId,
        user_id: state.currentUser.id,
        message: message.trim(),
        message_type: messageType
      });

      // Ideiglenes üzenet frissítése
      dispatch({
        type: actionTypes.UPDATE_MESSAGE,
        payload: {
          chatId,
          temp_id: tempId,
          updates: {
            id: result.message_id,
            created_at: result.created_at,
            sending: false,
            sent: true
          }
        }
      });

      // WebSocket üzenet küldése (új protokoll)
      webSocketService.sendChatMessage(
        chatId,
        state.currentUser.id,
        state.currentUser.name,
        message.trim(),
        messageType
      );

      // Chat lista frissítése
      dispatch({
        type: actionTypes.UPDATE_CHAT,
        payload: {
          chat_id: chatId,
          last_message: message.trim(),
          last_message_time: result.created_at
        }
      });

    } catch (error) {
      // Hiba esetén az ideiglenes üzenet jelölése
      dispatch({
        type: actionTypes.UPDATE_MESSAGE,
        payload: {
          chatId,
          temp_id: tempId,
          updates: {
            sending: false,
            error: true,
            errorMessage: error.message
          }
        }
      });
    }
  }, [state.currentUser]);

  // Chat létrehozása
  const createChat = useCallback(async (chatData) => {
    try {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      
      const newChat = await chatAPI.createChat({
        ...chatData,
        created_by: state.currentUser.id
      });

      dispatch({ type: actionTypes.ADD_CHAT, payload: newChat });
      
      // WebSocket értesítés küldése a chat tagoknak
      if (webSocketService.getConnectionStatus() === 'connected') {
        webSocketService.sendMessage({
          type: 'notify_new_chat',
          chat_id: newChat.chat_id,
          chat: newChat,
          users: chatData.users || [],
          created_by: state.currentUser.id,
          creator_name: state.currentUser.name
        });
      }
      
      // Automatikus kiválasztás
      await selectChat(newChat);
      
      return newChat;
    } catch (error) {
      dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
    }
  }, [state.currentUser, selectChat]);

  // Typing jelzés küldése
  const sendTypingStatus = useCallback((chatId, isTyping) => {
    if (state.currentUser && state.connectionStatus === 'connected') {
      webSocketService.sendTypingStatus(chatId, isTyping);
    }
  }, [state.currentUser, state.connectionStatus]);

  // Időbélyeg normalizálása (UTC-ről helyi időzónára)
  function normalizeTimestamp(input) {
    if (!input && input !== 0) return new Date().toISOString();

    let date;
    
    if (typeof input === 'number') {
      const ms = input < 1e12 ? input * 1000 : input;
      date = new Date(ms);
    } else if (typeof input === 'string') {
      // Ha már ISO formátum, akkor közvetlenül
      if (input.endsWith('Z') || input.includes('T')) {
        date = new Date(input);
      } else {
        // "YYYY-MM-DD HH:mm:ss" formátum - UTC-ként kezeljük
        const m = input.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
        if (m) {
          // UTC időpont létrehozása
          date = new Date(Date.UTC(
            Number(m[1]), Number(m[2]) - 1, Number(m[3]),
            Number(m[4]), Number(m[5]), Number(m[6])
          ));
        } else {
          date = new Date(input);
        }
      }
    } else {
      date = new Date(input);
    }

    if (isNaN(date.getTime())) {
      return new Date().toISOString();
    }

    // Helyi időzóna konverzió (nem UTC offset, hanem tényleges helyi idő)
    const localDate = new Date(date.getTime());
    return localDate.toISOString();
  }

  // Context értékek
  const value = {
    // Állapot
    ...state,
    
    // Műveletek
    loadChats,
    selectChat,
    loadMessages,
    sendMessage,
    createChat,
    sendTypingStatus,
    
    // WebSocket
    reconnectWebSocket: () => webSocketService.reconnect()
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

// Hook a context használatához
export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}

export default ChatContext;
